import type { Option } from "../types/v2/UI";

/**
 * The zone used for shops that have no configured timeZone. A one-time backfill
 * (scripts/backfillShopTimeZone.js) stamps this onto existing shops, so in
 * practice this constant is a backstop for newly-created shops rather than the
 * live path.
 */
export const DEFAULT_SHOP_TIME_ZONE = "America/Chicago";

// TS lib target for this project doesn't include ES2022.Intl, so
// Intl.supportedValuesOf isn't declared even though it's available at
// runtime (Node 18+, all modern browsers). Narrow-cast rather than widen the
// project-wide tsconfig lib list for one function.
type IntlWithSupportedValuesOf = typeof Intl & {
  supportedValuesOf?: (key: "timeZone") => string[];
};

/**
 * Whether a value is an IANA time zone this runtime can resolve.
 * @param {unknown} tz - The candidate zone.
 * @returns {boolean} True when Intl accepts it.
 */
export function isValidTimeZone(tz: unknown): tz is string {
  if (typeof tz !== "string" || tz.trim() === "") return false;
  try {
    const intl = Intl as IntlWithSupportedValuesOf;
    if (typeof intl.supportedValuesOf === "function") {
      // Canonical IANA zone list. Stricter than Intl.DateTimeFormat's
      // constructor, which leniently accepts non-IANA abbreviations like
      // "CST" without throwing.
      return intl.supportedValuesOf("timeZone").includes(tz);
    }
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * The zone the current browser is in, used to pre-fill the Settings control so
 * an admin sees the right answer already selected rather than a blank field.
 * @returns {string} An IANA zone, falling back to DEFAULT_SHOP_TIME_ZONE.
 */
export function detectBrowserTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(tz) ? tz : DEFAULT_SHOP_TIME_ZONE;
  } catch {
    return DEFAULT_SHOP_TIME_ZONE;
  }
}

export const SHOP_TIME_ZONE_OPTIONS: Option[] = [
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Phoenix", label: "Mountain – no DST (Phoenix)" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska (Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Honolulu)" },
];
